// src/utils/tokenStore.ts
//
// TEMPORARY Bearer-token fallback store. Remove once the API is served from a
// first-party subdomain (api.mednecessity.ai) and the auth cookies become
// first-party again.
//
// WHY THIS EXISTS:
// The API runs on a different site (*.azurewebsites.net) than the SPA, so the
// httpOnly `auth_token` / `refresh_token` cookies are THIRD-PARTY and are
// silently dropped by Safari ITP, all iOS browsers, Chrome Incognito, Brave,
// and strict-privacy configs. Those users could pass MFA but never hold a
// session — they were stuck in an endless login/MFA loop. Until first-party
// cookies are restored, we ALSO carry the JWTs here and send the access token
// as `Authorization: Bearer`. The cookie remains the PRIMARY credential (the
// backend reads it first); this store is a strictly additive fallback.
//
// SECURITY NOTE (deliberate, documented, temporary):
// localStorage is readable by JS (XSS exposure) and persists on disk — a
// conscious, short-lived deviation from the app's "no identity on disk"
// posture. The refresh token (7-day) is the sensitive part, so it is bound to
// the server session: /api/refresh rejects a refresh token whose session was
// logged out or has gone idle, AND mints access tokens that carry sessionId so
// the same logout/idle revocation applies on every request. Net effect — a
// stolen refresh token is dead the moment the victim logs out or idles out; it
// can NOT be replayed to mint session-less tokens (the bug the auth review
// caught). Residual XSS window: an attacker who lands script DURING an active
// session can replay the token until that session logs out / idles (45 min
// default) — the same class of exposure as the access token, just longer.
// Bounded further by: (1) tokens are cleared on logout and on refresh failure,
// (2) a password change still revokes everything, (3) this whole module is
// deleted when first-party cookies land. Do NOT broaden its use, and do NOT
// remove the /api/refresh session-binding without re-introducing this gap.

const ACCESS_KEY = 'mn_at';
const REFRESH_KEY = 'mn_rt';

// In-memory mirror so the tokens survive even if storage throws (private mode).
let memAccess: string | null = null;
let memRefresh: string | null = null;

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string | null): void {
  try {
    if (value == null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    /* private mode / quota — in-memory mirror still holds the value */
  }
}

export function getAccessToken(): string | null {
  return memAccess ?? safeGet(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return memRefresh ?? safeGet(REFRESH_KEY);
}

/**
 * Persist whichever tokens are provided. Pass a field as `undefined` to leave
 * it untouched; pass `null` to explicitly clear just that field.
 */
export function setTokens(tokens: { token?: string | null; refreshToken?: string | null }): void {
  if (tokens.token !== undefined) {
    memAccess = tokens.token;
    safeSet(ACCESS_KEY, tokens.token);
  }
  if (tokens.refreshToken !== undefined) {
    memRefresh = tokens.refreshToken;
    safeSet(REFRESH_KEY, tokens.refreshToken);
  }
}

export function clearTokens(): void {
  memAccess = null;
  memRefresh = null;
  safeSet(ACCESS_KEY, null);
  safeSet(REFRESH_KEY, null);
}
